import { Box, Stack } from '@mui/material'
import {
    FeatureFlagIdAccessExplorer,
} from 'c1gen/features'
import { C1IconButton, C1Tooltip } from 'c1ui'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { RelativeTimestamp } from 'components/core/time/display'
import { useFeatureChecker } from 'components/no-ui/capability'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserAccountType } from 'components/pages/app/users/account_type'
import { AppUserActionOptions, AppUserActionsDropdown } from 'components/pages/app/users/actions/AppUserActionsDropdown'
import { AppUserManageGrantsButton } from 'components/pages/app/users/actions/AppUserManageGrants'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { AppUserRolesWithFetch } from 'components/pages/app/users/roles/AppUserRolesWithFetch'
import { AppUserStatus } from 'components/pages/app/users/status/AppUserStatus'
import { InventoryAlertNotification } from 'components/pages/explore/inventory/inventory_tabs/InventoryAlertNotification'
import { UserDisplayNameWithFetch } from 'components/pages/user/user-display-name'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { Network } from 'lucide-react'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

const dropdownOptions = [
    AppUserActionOptions.setAccountType,
    AppUserActionOptions.setIdentity,
    AppUserActionOptions.clearIdentity,
    AppUserActionOptions.resetIdentity,
]

export const cols: Array<C1TableReduxColumn<AppUserValue>> = [
    {
        field: 'account',
        label: <FormattedMessage id="components_user_list_account" defaultMessage="Account" />,
        flex: 2,
        Component: ({ row: appUser }) => <AppUserDisplayName appUser={appUser} showTooltip />,
    },
    {
        field: 'app',
        label: <FormattedMessage id="components_user_list_app" defaultMessage="Application" />,
        Component: ({ row: appUser }) => <Box>
            {appUser.app ?
                <AppDisplayName margin={false} app={appUser.app} /> :
                <C1EmptyIndicator />
            }
        </Box>,
    },
    {
        field: 'status',
        label: <FormattedMessage id="components_user_list_account_status" defaultMessage="Status" />,
        Component: ({ row: appUser }) => <AppUserStatus appUser={appUser} />,
    },
    {
        field: 'accountType',
        label: <FormattedMessage id="components_user_list_account_type" defaultMessage="Account type" />,
        Component: ({ row: appUser }) => <AppUserAccountType appUser={appUser} />,
    },
    {
        field: 'accountRoles',
        label: <FormattedMessage id="components_user_list_roles" defaultMessage="Roles" />,
        Component: ({ row: appUser }) => <AppUserRolesWithFetch appId={appUser.appId} appUserId={appUser.id} />,
    },
    {
        field: 'accountOwner',
        label: <FormattedMessage id="components_user_list_account_responsible_party" defaultMessage="Account owner" />,
        Component: ({ row: appUser }) => <Box>
            {appUser.identityUserId ?
                <UserDisplayNameWithFetch
                    margin={false}
                    id={appUser.identityUserId}
                    showTooltip
                    showUserStatus
                /> :
                <C1EmptyIndicator />
            }
        </Box>,
    },
    {
        field: 'lastLogin',
        label: <FormattedMessage id="components_user_list_last_login" defaultMessage="Last login" />,
        Component: ({ row: appUser }) => <Box>
            {appUser.lastUsedAt && appUser.lastUsedAt.seconds ?
                <RelativeTimestamp variant="date_only" date={appUser.lastUsedAt} /> :
                <C1EmptyIndicator />
            }
        </Box>,
    },
    {
        field: 'actions',
        isSticky: () => true,
        Component: ({ row: appUser }: { row: AppUserValue }): ReactElement => {
            const intl = useIntl()
            const hasAccessExplorerFF = useFeatureChecker(FeatureFlagIdAccessExplorer)
            const graphToolTipText = intl.formatMessage({
                id: 'components_user_load_in_graoh',
                defaultMessage: 'Load in graph',
            })
            return <Stack direction="row" spacing={1} px={1}>
                <InventoryAlertNotification appId={appUser.appId} appUserId={appUser.id} alertCount={appUser.alertCount || 0} />
                {hasAccessExplorerFF && <C1Tooltip title={graphToolTipText}>
                    <Box>
                        <C1IconButton
                            color="info"
                            variant="outlined"
                            href={`/admin/explore/graph?appUserId=${appUser.id}&appId=${appUser.appId}`}
                            aria-label={graphToolTipText}
                        >
                            <Network />
                        </C1IconButton>
                    </Box>
                </C1Tooltip>
                }
                <AppUserManageGrantsButton appUser={appUser} revokeVisibleOnly={false} />
                <AppUserActionsDropdown
                    appUser={appUser}
                    options={dropdownOptions}
                />
            </Stack>
        },
    },
]
